import './App.css';
import './styles/optimizations.css';
import React, { useState, useEffect, Suspense, lazy, memo } from 'react';

// Lazy load components for better performance
const Navbar = lazy(() => import('./Web-Components/navbar.jsx'));
const Home = lazy(() => import('./Web-Components/home.jsx'));
const About = lazy(() => import('./Web-Components/about.jsx'));
const Education = lazy(() => import('./Web-Components/education.jsx'));
const Services = lazy(() => import('./Web-Components/services.jsx'));
const Skills = lazy(() => import('./Web-Components/skills.jsx'));
const Testimonials = lazy(() => import('./Web-Components/testimonials.jsx'));
const Projects = lazy(() => import('./Web-Components/projects.jsx'));
const Contact = lazy(() => import('./Web-Components/contact.jsx'));
const Footer = lazy(() => import('./Web-Components/footer.jsx'));
// Optimized loading component with memo
const LoadingSpinner = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400 text-lg">Loading...</p>
    </div>
  </div>
));

function App() {
  // Theme state management
  const [theme, setTheme] = useState('');
  const [bodyColor, setBodyColor] = useState('#FAFAFA');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize theme from localStorage and register service worker
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
      setBodyColor(savedTheme === 'dark' ? '#111827' : '#FAFAFA');
    }
    
    // Register service worker for better performance (disabled in development)
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('SW registered: ', registration);
        })
        .catch(registrationError => {
          console.log('SW registration failed: ', registrationError);
        });
    }
    
    // Optimized loading time for better performance
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Update body class and background color
  useEffect(() => {
    document.body.className = theme;
    document.body.style.backgroundColor = bodyColor;
    localStorage.setItem('theme', theme);
  }, [theme, bodyColor]);

  // Toggle dark mode
  const toggleMode = () => {
    setTheme(theme === '' ? 'dark' : '');
    setBodyColor(bodyColor === '#FAFAFA' ? '#111827' : '#FAFAFA');
  };

  // Icon state management
  const [iconClass, setIconClass] = useState("ri-moon-line");

  const changeIconClass = () => {
    setIconClass((prevValue) =>
      prevValue === "ri-moon-line" ? "ri-sun-line" : "ri-moon-line"
    );
  };

  // Show loading spinner while components load
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="App">
      <Suspense fallback={<LoadingSpinner />}>
        <Navbar 
          toggleMode={toggleMode} 
          changeIconClass={changeIconClass} 
          iconClass={iconClass} 
          bodyColor={bodyColor} 
        />
        <Home bodyColor={bodyColor} />
        <About />
        <Education />
        <Skills />
        <Services />
        <Testimonials />
        <Projects />
        <Contact />
        <Footer />
      </Suspense>
    </div>
  );
}

export default App;
